import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import { RecordResponse } from '../models/ServiceTypes';
import { WARootState } from './store';
import { filterDashboardWidgetsForRole } from './dashboardWidgetFilters';

export interface DashboardWidget {
  IsHalfWidth: boolean;
  WidgetId: number;
  DashboardId: number;
  Name: string;
  Code: string;
  Description: string;
  Title: string;
  Image: string;
  Position: number;
  IsVisible: boolean;
  IsEdited: boolean;
  UserDashboardWidgetExceptionsId: number | null;
  Settings: Array<{ key: string; value: string }>;
}

export type DashboardWidgetsResponse = RecordResponse<DashboardWidget>;

export const dashboardApi = createApi({
  reducerPath: 'investorApi',
  baseQuery: waServicesQuery,
  tagTypes: ['Dashboard'],
  endpoints: (builder) => ({
    getDashboardWidgets: builder.query<
      DashboardWidgetsResponse,
      { dashboardName: string }
    >({
      queryFn: async ({ dashboardName }, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: `/api/${currentRole}/DashboardWidgets/`,
          method: 'GET',
          params: {
            DashboardName: `${currentRole?.toLowerCase()} ${dashboardName}`,
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        const data = result.data as DashboardWidgetsResponse;

        // This filter runs centrally in the data layer so dashboard consumers
        // (e.g. `dashboardAdvisorView.tsx`, `customizeDashboardStep2.tsx`)
        // respect role-specific widget availability.
        return { data: filterDashboardWidgetsForRole(data, currentRole) };
      },
      providesTags: ['Dashboard'],
      keepUnusedDataFor: 24000,
    }),
    editWidgetDashboardVisibility: builder.mutation<
      void,
      { widget: DashboardWidget; isVisible: boolean }
    >({
      queryFn: async ({ widget, isVisible }, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        // Pass the updated fields to the body
        const updatedWidget = {
          ...widget,
          IsVisible: isVisible,
          // Ensure we're also sending widget.Position, or any other fields
          Position: widget.Position,
          IsHalfWidth: widget.IsHalfWidth,
        };

        let url = `/api/${currentRole}/DashboardWidgets/${widget.UserDashboardWidgetExceptionsId}`;
        let method = 'PUT';

        if (widget.UserDashboardWidgetExceptionsId === null) {
          url = `/api/${currentRole}/DashboardWidgets/`;
          method = 'POST';
        }

        const result = await baseQuery({
          url,
          method,
          body: updatedWidget,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: undefined };
      },
      invalidatesTags: ['Dashboard'],
    }),
  }),
});

export const {
  useGetDashboardWidgetsQuery,
  useEditWidgetDashboardVisibilityMutation,
} = dashboardApi;
